import { Link } from "react-router-dom";
import { categoryLabels, centsToDollars, urgencyLabels } from "../opportunities/api.js";
import { isThreadUnread, type PortalOpportunityListItem } from "./api.js";

/**
 * One dashboard row (unit 17), shared by the Opportunities, Quotes, and
 * Inbox tabs. Status chips: live invitation state while open, outcome once
 * decided. The unread dot is client-side last-seen state (no read-receipt
 * events — unit 14 decision).
 */
export function OpportunityCard({ item }: { item: PortalOpportunityListItem }) {
  const opp = item.opportunity;
  const unread = isThreadUnread(item);

  return (
    <Link
      to={`/portal/opportunities/${item.invitationId}`}
      className="block rounded-2xl border border-charcoal/15 bg-white p-4 active:bg-field-gray"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-base font-semibold text-charcoal">
            {opp ? (categoryLabels[opp.serviceCategory] ?? opp.serviceCategory) : "Request"}
            {unread && (
              <span
                className="ml-2 inline-block h-2.5 w-2.5 rounded-full bg-brand-600 align-middle"
                role="status"
                aria-label="New message"
              />
            )}
          </p>
          {opp && (
            <p className="mt-0.5 truncate text-sm text-charcoal">
              {opp.customerFirstName} · {opp.location.city}, {opp.location.state}
            </p>
          )}
        </div>
        <StatusChip item={item} />
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-charcoal">
        {item.outcome === "open" && opp && (
          <span className="font-medium text-amber-700">
            {urgencyLabels[opp.urgency] ?? opp.urgency}
          </span>
        )}
        {item.quote && <span>Quoted {quoteSummary(item)}</span>}
        {item.lastMessage && (
          <span>Last message {new Date(item.lastMessage.sentAt).toLocaleDateString()}</span>
        )}
        <span>Invited {new Date(item.createdAt).toLocaleDateString()}</span>
      </div>
    </Link>
  );
}

function StatusChip({ item }: { item: PortalOpportunityListItem }) {
  const { label, classes } =
    item.outcome === "open"
      ? {
          INVITED: { label: "New", classes: "bg-brand-50 text-brand-800" },
          VIEWED: { label: "Viewed", classes: "bg-field-gray text-charcoal" },
          QUOTED: { label: "Quoted", classes: "bg-brand-50 text-brand-800" },
          REVOKED: { label: "Closed", classes: "bg-field-gray text-charcoal" },
          EXPIRED: { label: "Expired", classes: "bg-field-gray text-charcoal" },
        }[item.invitationStatus]
      : {
          won: { label: "You won this job", classes: "bg-emerald-700 text-white" },
          filled: { label: "Filled", classes: "bg-field-gray text-charcoal" },
          closed: { label: "Closed", classes: "bg-field-gray text-charcoal" },
        }[item.outcome];

  return (
    <span
      className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold whitespace-nowrap ${classes}`}
    >
      {label}
    </span>
  );
}

function quoteSummary(item: PortalOpportunityListItem): string {
  const q = item.quote;
  if (!q) return "";
  if (q.type === "fixed" && q.amount) return centsToDollars(q.amount.amountCents);
  if (q.type === "range" && q.minAmount && q.maxAmount) {
    return `${centsToDollars(q.minAmount.amountCents)}–${centsToDollars(q.maxAmount.amountCents)}`;
  }
  return "site visit";
}
